import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component } from 'react';

import { withSize } from 'react-sizeme';
import regression from 'regression';
import {
    LineSegment,
    VictoryAxis,
    VictoryChart,
    VictoryCursorContainer,
    VictoryLabel,
    VictoryLine,
    VictoryScatter,
    VictoryTooltip,
} from 'victory';
import { _ } from '/@/locale';
import { range } from '/@/utils';

import styles from './styles.module.css';

const colors = {
    primary: '#A27749',
    primaryDark: '#5B350C',
    grayLight: '#E5E5E5',
    gray: '#BFBFBF',
    grayDark: '#909090',
};

const textStyles = {
    fill: colors.grayDark,
    fontFamily: 'inherit',
    fontSize: 15,
};

@withSize()
@autobind
export default class LinearRegressionChart extends Component {
    static propTypes = {
        data: PropTypes.shape({
            head: PropTypes.shape({
                id: PropTypes.number.isRequired,
                name: PropTypes.string.isRequired,
            }).isRequired,
            other: PropTypes.shape({
                id: PropTypes.number.isRequired,
                name: PropTypes.string.isRequired,
            }).isRequired,
            value: PropTypes.number.isRequired,
            result: PropTypes.oneOf(['win', 'lose', 'tie']).isRequired,
            trialCount: PropTypes.number,
            winRate: PropTypes.number,
        }).isRequired,
        renderValue: PropTypes.func.isRequired,
        crop: PropTypes.oneOf(['corn', 'soy']).isRequired,
        user: PropTypes.shape({
            email: PropTypes.string.isRequired,
        }).isRequired,
    };

    static contextTypes = {
        units: PropTypes.object.isRequired,
    };

    render() {
        const { data, user } = this.props;
        const chartWidth = this.props.size.width;
        const chartHeight = Math.max(300, chartWidth / 2);
        const padding = { top: 30, bottom: 30, left: 60, right: 0 };
        const { headPoints, otherPoints, minX, minY } = this.parseData(data);
        const { xLabelPosition, yLabelPosition } = this.calculateLabelPositions({ chartWidth, chartHeight, padding });

        return (
            <div className={styles.root}>
                <VictoryChart
                    width={chartWidth}
                    height={chartHeight}
                    padding={padding}
                    domainPadding={10}
                    minDomain={{ x: minX - 100, y: minY - 100 }}
                    containerComponent={this.renderContainerComponent()}
                >
                    {this.renderXAxis({ xLabelPosition })}
                    {this.renderYAxis({ yLabelPosition })}
                    {this.renderPoints({ points: headPoints, color: colors.primary })}
                    {this.renderPoints({ points: otherPoints, color: colors.gray })}
                    {this.renderRegression({ points: headPoints, color: colors.primary })}
                    {this.renderRegression({ points: otherPoints, color: colors.grayDark })}
                </VictoryChart>
                <div className={`${styles.watermark} mp-mask`}>
                    {range(4).map((line) => (
                        <div key={line} className={styles.line}>
                            {range(10).map(() => `${user.email} `)}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderContainerComponent() {
        const { crop } = this.props;
        const { units } = this.context;

        const cursorLabel = ({ datum }) => {
            const x = units.toYield(datum.x, crop).toString();
            const y = units.toYield(datum.y, crop).toString();
            return `${_('Ambiente')}: ${x}\n${_('Rendimiento')}: ${y}`;
        };

        return (
            <VictoryCursorContainer
                cursorLabel={cursorLabel}
                cursorLabelOffset={{ x: 0, y: 0 }}
                cursorLabelComponent={
                    <VictoryTooltip
                        style={textStyles}
                        flyoutStyle={{ strokeWidth: 0, fill: colors.grayLight }}
                        cornerRadius={4}
                        constrainToVisibleArea
                        pointerLength={20}
                    />
                }
                cursorComponent={
                    <LineSegment
                        style={{
                            stroke: colors.gray,
                            strokeDasharray: '2,4',
                            strokeWidth: 2,
                        }}
                    />
                }
            />
        );
    }

    renderXAxis({ xLabelPosition }) {
        return (
            <VictoryAxis
                fixLabelOverlap
                label={`${_('Ambiente')} (${this.context.units.getUnit('yield', this.props.crop)})`}
                axisLabelComponent={<VictoryLabel {...xLabelPosition} renderInPortal />}
                tickFormat={(value) => this.context.units.toYield(value, this.props.crop).localeValue}
                style={{
                    axis: {
                        stroke: colors.grayLight,
                        strokeWidth: 2,
                    },
                    axisLabel: {
                        ...textStyles,
                        textAnchor: 'end',
                        verticalAnchor: 'end',
                    },
                    tickLabels: {
                        ...textStyles,
                        textAnchor: (axis) => {
                            if (axis.ticks[0] === axis.tick) return 'start';
                            if (axis.ticks[axis.ticks.length - 1] === axis.tick) return 'end';
                            return 'middle';
                        },
                    },
                }}
            />
        );
    }

    renderYAxis({ yLabelPosition }) {
        return (
            <VictoryAxis
                dependentAxis
                fixLabelOverlap
                label={`${_('Rendimiento')} (${this.context.units.getUnit('yield', this.props.crop)})`}
                axisLabelComponent={<VictoryLabel {...yLabelPosition} angle={0} renderInPortal />}
                tickFormat={(value) => this.context.units.toYield(value, this.props.crop).localeValue}
                style={{
                    grid: {
                        stroke: colors.grayLight,
                    },
                    axis: {
                        stroke: colors.grayLight,
                        strokeWidth: 2,
                    },
                    axisLabel: {
                        ...textStyles,
                        textAnchor: 'start',
                    },
                    tickLabels: {
                        ...textStyles,
                    },
                }}
            />
        );
    }

    renderPoints({ points, color }) {
        return (
            <VictoryScatter
                size={4}
                data={points}
                style={{
                    data: {
                        fill: color,
                    },
                }}
            />
        );
    }

    renderRegression({ points, color }) {
        const result = regression.linear(points.map(({ x, y }) => [x, y]));
        const [m, b] = result.equation;

        return (
            <VictoryLine
                y={(datum) => m * datum.x + b}
                style={{
                    data: {
                        stroke: color,
                        strokeWidth: 2,
                    },
                }}
            />
        );
    }

    parseData(data) {
        const mapTrialToPoints = (trial) => ({
            x: trial.environment,
            y: trial.result,
        });

        const headPoints = data.trialsByProduct[data.head.id].map(mapTrialToPoints);
        const otherPoints = data.trialsByProduct[data.other.id].map(mapTrialToPoints);
        const allPoints = [...headPoints, ...otherPoints];

        const minX = allPoints.map(({ x }) => x).sort((a, b) => a - b)[0];
        const minY = allPoints.map(({ y }) => y).sort((a, b) => a - b)[0];

        return { headPoints, otherPoints, minX, minY };
    }

    calculateLabelPositions({ chartWidth, chartHeight, padding }) {
        return {
            xLabelPosition: {
                x: chartWidth,
                y: chartHeight - padding.bottom - 10,
            },
            yLabelPosition: {
                x: padding.left + 10,
                y: 20,
            },
        };
    }
}
